import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Eye, EyeOff, Lock, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '../utils/config';
import { isHomeSmartSite, HOME_SMART_LOGIN } from '../utils/brandSession';
import { formatApiErrorDetail } from '../utils/errorDetail';


const ResetPassword = ({ brand }) => {
  const isHomeSmart = brand === 'home-smart' || isHomeSmartSite();
  const goToLogin = () => window.location.assign(isHomeSmart ? HOME_SMART_LOGIN : '/login');
  const location = useLocation();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  useEffect(() => { document.title = isHomeSmart ? 'Home Smart · Nuova password' : 'HWsmart · Nuova password'; }, [isHomeSmart]);

  useEffect(() => {
    // Get token from URL query params
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get('token');
    
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setError('Token mancante. Il link potrebbe non essere valido.');
    }
  }, [location]);

  const validatePassword = (password) => {
    if (Array.from(password).length < 8) {
      return 'La password deve essere lunga almeno 8 caratteri';
    }
    if (new TextEncoder().encode(password).length > 72) return 'Password troppo lunga. Riduci il numero di caratteri.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Le password non corrispondono');
      setLoading(false);
      return;
    }

    // Validate password strength
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      setLoading(false);
      return;
    }

    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token: token,
        new_password: newPassword
      }, {params: {brand: isHomeSmart ? 'home-smart' : undefined}});

      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        goToLogin();
      }, 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail, 'Errore durante il reset della password'));
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4" data-testid="password-reset-success">
        <Card className="w-full max-w-md p-8 shadow-2xl border-0">
          <div className="text-center">
            {isHomeSmart && <img src="/home-smart-logo.png" alt="Home Smart" className="h-20 w-20 mx-auto mb-4 object-contain" data-testid="password-reset-success-logo" />}
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Password Reimpostata!</h2>
            <p className="text-slate-600 mb-4">
              La tua password è stata modificata con successo.
            </p>
            <p className="text-sm text-slate-500">
              Verrai reindirizzato alla pagina di login tra pochi secondi...
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4" data-testid={isHomeSmart ? 'home-smart-reset-page' : 'hw-smart-reset-page'}>
      <Card className="w-full max-w-md p-8 shadow-2xl border-0">
        <div className="text-center mb-8">
          {isHomeSmart && <img src="/home-smart-logo.png" alt="Home Smart" className="h-20 w-20 mx-auto mb-4 object-contain" data-testid="password-reset-logo" />}
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <Lock className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Nuova Password</h1>
          <p className="text-slate-600">
            Inserisci la tua nuova password per l'account {isHomeSmart ? 'Home Smart' : 'HWsmart'}
          </p>
        </div>

        {!token ? (
          <div data-testid="password-reset-invalid-alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-3">
            <XCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium mb-1">Link non valido</p>
              <p>Il link di reset password potrebbe essere scaduto o non valido.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="newPassword" className="text-slate-700 font-medium">
                Nuova Password
              </Label>
              <div className="relative mt-1">
                <Input
                  id="newPassword"
                  data-testid="password-reset-new-input"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  disabled={loading}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="password-reset-new-visibility"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Minimo 8 caratteri
              </p>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-slate-700 font-medium">
                Conferma Password
              </Label>
              <div className="relative mt-1">
                <Input
                  id="confirmPassword"
                  data-testid="password-reset-confirm-input"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  disabled={loading}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  data-testid="password-reset-confirm-visibility"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid="password-reset-error-alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="password-reset-submit-button"
              disabled={loading || !token}
              className="w-full bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700 text-white py-3 text-lg"
            >
              {loading ? 'Reimpostazione...' : 'Reimposta Password'}
            </Button>

            <div className="text-center mt-4">
              <button
                type="button"
                onClick={goToLogin}
                data-testid="password-reset-back-button"
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                Torna al login
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
};

export default ResetPassword;
